import React, { useState, useMemo, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import {
  TrendingUp,
  DollarSign,
  BarChart3,
  PieChartIcon,
  ArrowUpRight,
  ArrowDownRight,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { Card } from '../ui';
import useSocialMediaStore from '../../stores/socialMediaStore';
import useGlobalCustomerStore from '../../stores/globalCustomerStore';
import { formatEMV } from '../../utils/emvCalculator';
import { socialMediaService } from '../../services/socialMediaService';
import { toastService, getErrorMessage } from '../../services/toastService';

const EMVCharts: React.FC = () => {
  const [selectedTimeframe] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [selectedPlatform] = useState<'all' | 'instagram' | 'tiktok'>('all');
  const [selectedProfiles] = useState<string[]>([]);
  // const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [selectedHashtag, _setSelectedHashtag] = useState<string>('');
  const [emvOverTimeData, setEmvOverTimeData] = useState<any>(null);
  const [, setEmvLoading] = useState(false);
  const [, setHashtagAnalytics] = useState<any>(null);
  const [, setHashtagLoading] = useState(false);
  const [, setHashtagPosts] = useState<any>(null);
  const [, setHashtagPostsLoading] = useState(false);
  const [emvByPostTypeData, setEmvByPostTypeData] = useState<any>(null);
  const [, setEmvByPostTypeLoading] = useState(false);
  const [emvByAuthorTypeData, setEmvByAuthorTypeData] = useState<any>(null);
  const [, setEmvByAuthorTypeLoading] = useState(false);

  const {
    posts,
    // profiles,
    currentCustomerId,
    dashboardAnalytics,
    // topPosts: apiTopPosts,
    // loading,
    currentPeriod,
    selectedPlatformIds,
    selectedAccountIds,
    selectedPostType,
    accountType,
    customDateRange
  } = useSocialMediaStore();

  const { getAPIFilterParams, selectedAccount } = useGlobalCustomerStore();

  // Load EMV over time data from API when customer is selected
  useEffect(() => {
    const loadEmvOverTime = async () => {
      if (!currentCustomerId) {
        setEmvOverTimeData(null);
        return;
      }

      try {
        setEmvLoading(true);
        const globalFilterParams = getAPIFilterParams();
        const hasGlobalAccountSelection = (globalFilterParams.accountIds?.length ?? 0) > 0;

        const params: any = {
          customerId: currentCustomerId,
          period: currentPeriod,
          ...globalFilterParams
        };

        if (selectedPlatformIds && selectedPlatformIds.length > 0) {
          params.platformIds = selectedPlatformIds;
        }

        if (hasGlobalAccountSelection) {
          // global store account takes precedence — already in params via spread
        } else if (selectedAccountIds && selectedAccountIds.length > 0) {
          params.accountIds = selectedAccountIds;
        } else if (accountType !== 'all') {
          // Scope to the account-type toggle so charts match the KPI cards
          // (mirrors socialMediaStore.loadDashboardAnalytics)
          params.categoryId = accountType === 'owned' ? 1 : 2;
        }

        if (selectedPostType && selectedPostType !== 'all') {
          params.postTypes = [selectedPostType];
        }

        if (currentPeriod === 'custom' && customDateRange) {
          params.customStartDate = customDateRange.start;
          params.customEndDate = customDateRange.end;
        }

        const response = await socialMediaService.getEmvOverTime(params);
        
        // Filter out future dates
        if (response.data?.dailyData) {
          const today = new Date();
          today.setHours(23, 59, 59, 999);
          
          const filteredData = {
            ...response.data,
            dailyData: response.data.dailyData.filter((item: any) => {
              const itemDate = new Date(item.date);
              return itemDate <= today;
            })
          };
          
          setEmvOverTimeData(filteredData);
        } else {
          setEmvOverTimeData(response.data);
        }
      } catch (error) {
        console.error('Failed to load EMV over time data:', error);
        try {
          const errorDetails = getErrorMessage(error);
          toastService.error(errorDetails.title, errorDetails.message || 'Failed to load EMV trend data');
        } catch (toastError) {
          console.error('Failed to show toast:', toastError);
        }
        setEmvOverTimeData(null);
      } finally {
        setEmvLoading(false);
      }
    };

    loadEmvOverTime();
  }, [currentCustomerId, currentPeriod, selectedPlatformIds, selectedAccountIds, selectedPostType, accountType, customDateRange, selectedAccount, getAPIFilterParams]);

  // Load hashtag analytics data from API when customer is selected
  useEffect(() => {
    const loadHashtagAnalytics = async () => {
      if (!currentCustomerId) {
        setHashtagAnalytics(null);
        return;
      }

      try {
        setHashtagLoading(true);
        const filterParams = getAPIFilterParams();
        const params: any = {
          ...filterParams,
          period: currentPeriod,
          limit: 20
        };

        // Fallback to currentCustomerId if no global selection
        if (!params.customerId && !params.accountIds && currentCustomerId) {
          params.customerId = currentCustomerId;
        }

        if (selectedPlatformIds && selectedPlatformIds.length > 0) {
          params.platformIds = selectedPlatformIds;
        }

        if (selectedAccountIds && selectedAccountIds.length > 0) {
          params.accountIds = selectedAccountIds;
        } else if (!params.accountIds && accountType !== 'all') {
          // Scope to the account-type toggle so charts match the KPI cards
          // (mirrors socialMediaStore.loadDashboardAnalytics)
          params.categoryId = accountType === 'owned' ? 1 : 2;
        }

        if (currentPeriod === 'custom' && customDateRange) {
          params.customStartDate = customDateRange.start;
          params.customEndDate = customDateRange.end;
        }

        const response = await socialMediaService.getHashtagAnalytics(params);
        setHashtagAnalytics(response.data);
      } catch (error) {
        console.error('Failed to load hashtag analytics:', error);
        if (error instanceof Error && error.message.includes('timeout')) {
          console.error('Request timed out after 30 seconds');
        }
        try {
          const errorDetails = getErrorMessage(error);
          toastService.error(errorDetails.title, errorDetails.message || 'Failed to load hashtag analytics');
        } catch (toastError) {
          console.error('Failed to show toast:', toastError);
        }
        setHashtagAnalytics(null);
      } finally {
        setHashtagLoading(false);
      }
    };

    loadHashtagAnalytics();
  }, [currentCustomerId, currentPeriod, selectedPlatformIds, selectedAccountIds, accountType, customDateRange, getAPIFilterParams, selectedAccount]);

  // Load hashtag posts when a hashtag is selected
  useEffect(() => {
    const loadHashtagPosts = async () => {
      if (!currentCustomerId || !selectedHashtag) {
        setHashtagPosts(null);
        return;
      }

      try {
        setHashtagPostsLoading(true);
        const cleanHashtag = selectedHashtag.startsWith('#') ? selectedHashtag.substring(1) : selectedHashtag;
        const filterParams = getAPIFilterParams();
        
        const params: any = {
          ...filterParams,
          hashtag: cleanHashtag,
          period: currentPeriod,
          limit: 10,
          sortBy: 'emv' as const
        };

        // Fallback to currentCustomerId if no global selection
        if (!params.customerId && !params.accountIds && currentCustomerId) {
          params.customerId = currentCustomerId;
        }

        if (selectedPlatformIds && selectedPlatformIds.length > 0) {
          params.platformIds = selectedPlatformIds;
        }

        if (selectedAccountIds && selectedAccountIds.length > 0) {
          params.accountIds = selectedAccountIds;
        } else if (!params.accountIds && accountType !== 'all') {
          // Scope to the account-type toggle so charts match the KPI cards
          // (mirrors socialMediaStore.loadDashboardAnalytics)
          params.categoryId = accountType === 'owned' ? 1 : 2;
        }

        if (currentPeriod === 'custom' && customDateRange) {
          params.customStartDate = customDateRange.start;
          params.customEndDate = customDateRange.end;
        }

        const response = await socialMediaService.getHashtagPosts(params);
        setHashtagPosts(response.data);
      } catch (error) {
        console.error('Failed to load hashtag posts:', error);
        setHashtagPosts(null);
      } finally {
        setHashtagPostsLoading(false);
      }
    };

    loadHashtagPosts();
  }, [currentCustomerId, selectedHashtag, currentPeriod, selectedPlatformIds, selectedAccountIds, accountType, customDateRange, getAPIFilterParams, selectedAccount]);

  // Load EMV by post type data from API when customer is selected
  useEffect(() => {
    const loadEmvByPostType = async () => {
      if (!currentCustomerId) {
        setEmvByPostTypeData(null);
        return;
      }

      try {
        setEmvByPostTypeLoading(true);
        const globalFilterParams = getAPIFilterParams();
        const hasGlobalAccountSelection = (globalFilterParams.accountIds?.length ?? 0) > 0;

        const params: any = {
          customerId: currentCustomerId,
          period: currentPeriod,
          ...globalFilterParams
        };

        if (selectedPlatformIds && selectedPlatformIds.length > 0) {
          params.platformIds = selectedPlatformIds;
        }

        if (hasGlobalAccountSelection) {
          // global store account takes precedence — already in params via spread
        } else if (selectedAccountIds && selectedAccountIds.length > 0) {
          params.accountIds = selectedAccountIds;
        } else if (accountType !== 'all') {
          // Scope to the account-type toggle so charts match the KPI cards
          // (mirrors socialMediaStore.loadDashboardAnalytics)
          params.categoryId = accountType === 'owned' ? 1 : 2;
        }

        if (selectedPostType && selectedPostType !== 'all') {
          params.postTypes = [selectedPostType];
        }

        if (currentPeriod === 'custom' && customDateRange) {
          params.customStartDate = customDateRange.start;
          params.customEndDate = customDateRange.end;
        }

        const response = await socialMediaService.getEmvByPostType(params);
        setEmvByPostTypeData(response.data);
      } catch (error) {
        console.error('Failed to load EMV by post type:', error);
        setEmvByPostTypeData(null);
      } finally {
        setEmvByPostTypeLoading(false);
      }
    };

    loadEmvByPostType();
  }, [currentCustomerId, currentPeriod, selectedPlatformIds, selectedAccountIds, selectedPostType, accountType, customDateRange, selectedAccount, getAPIFilterParams]);

  // Load EMV by author type data from API when customer is selected
  useEffect(() => {
    const loadEmvByAuthorType = async () => {
      if (!currentCustomerId) {
        setEmvByAuthorTypeData(null);
        return;
      }

      try {
        setEmvByAuthorTypeLoading(true);
        const globalFilterParams = getAPIFilterParams();
        const hasGlobalAccountSelection = (globalFilterParams.accountIds?.length ?? 0) > 0;

        const params: any = {
          customerId: currentCustomerId,
          period: currentPeriod,
          ...globalFilterParams
        };

        if (selectedPlatformIds && selectedPlatformIds.length > 0) {
          params.platformIds = selectedPlatformIds;
        }

        if (hasGlobalAccountSelection) {
          // global store account takes precedence — already in params via spread
        } else if (selectedAccountIds && selectedAccountIds.length > 0) {
          params.accountIds = selectedAccountIds;
        } else if (accountType !== 'all') {
          // Scope to the account-type toggle so charts match the KPI cards
          // (mirrors socialMediaStore.loadDashboardAnalytics)
          params.categoryId = accountType === 'owned' ? 1 : 2;
        }

        if (selectedPostType && selectedPostType !== 'all') {
          params.postTypes = [selectedPostType];
        }

        if (currentPeriod === 'custom' && customDateRange) {
          params.customStartDate = customDateRange.start;
          params.customEndDate = customDateRange.end;
        }

        const response = await socialMediaService.getEmvByAuthorType(params);
        setEmvByAuthorTypeData(response.data);
      } catch (error) {
        console.error('Failed to load EMV by author type:', error);
        setEmvByAuthorTypeData(null);
      } finally {
        setEmvByAuthorTypeLoading(false);
      }
    };

    loadEmvByAuthorType();
  }, [currentCustomerId, currentPeriod, selectedPlatformIds, selectedAccountIds, selectedPostType, accountType, customDateRange, selectedAccount, getAPIFilterParams]);

  // Filter posts based on timeframe, platform, and profiles
  const filteredPosts = useMemo(() => {
    const now = new Date();
    const daysMap = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 };
    const days = daysMap[selectedTimeframe];
    const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    return posts.filter(post => {
      const postDate = new Date(post.timestamp);
      const platformMatch = selectedPlatform === 'all' || post.platform === selectedPlatform;
      const profileMatch = selectedProfiles.length === 0 || selectedProfiles.includes(post.authorUsername);
      const dateMatch = postDate >= cutoffDate;
      return platformMatch && profileMatch && dateMatch;
    });
  }, [posts, selectedTimeframe, selectedPlatform, selectedProfiles]);

  // Prepare EMV trend data
  const emvTrendData = useMemo(() => {
    // Use API data if available and customer is selected
    if (emvOverTimeData && currentCustomerId) {
      return emvOverTimeData.dailyData.map((item: any) => ({
        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        emv: item.totalEmvValue,
        posts: item.postCount,
        EMV: item.totalEmvValue // For tooltip formatting
      }));
    }

    // Fallback to local data
    const dataMap = new Map<string, {
      date: string;
      emv: number;
      posts: number;
      instagram: number;
      tiktok: number;
    }>();

    filteredPosts.forEach(post => {
      const date = new Date(post.timestamp).toISOString().split('T')[0];
      
      if (!dataMap.has(date)) {
        dataMap.set(date, {
          date,
          emv: 0,
          posts: 0,
          instagram: 0,
          tiktok: 0
        });
      }

      const dayData = dataMap.get(date)!;
      dayData.emv += post.emv;
      dayData.posts += 1;
      (dayData as any)[post.platform] = ((dayData as any)[post.platform] || 0) + post.emv;
    });

    return Array.from(dataMap.values())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(item => ({
        ...item,
        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }));
  }, [emvOverTimeData, currentCustomerId, filteredPosts]);

  // Prepare platform comparison data
  // Prepare EMV breakdown pie chart data
  const emvBreakdownData = useMemo(() => {
    // Helper function to parse EMV string (e.g., "$971.84" -> 971.84)
    const parseEmvValue = (emvString: string): number => {
      if (typeof emvString === 'number') return emvString;
      if (!emvString) return 0;
      return parseFloat(emvString.replace(/[$,]/g, '')) || 0;
    };

    // Use dashboard analytics if available and customer is selected
    if (dashboardAnalytics && currentCustomerId) {
      const metrics = dashboardAnalytics.metrics;

      return [
        {
          name: 'Views',
          value: parseEmvValue(metrics.totalViews.emv?.current || '0'),
          count: metrics.totalViews.current,
          color: '#3b82f6'
        },
        {
          name: 'Likes',
          value: parseEmvValue(metrics.totalLikes.emv?.current || '0'),
          count: metrics.totalLikes.current,
          color: '#10b981'
        },
        {
          name: 'Comments',
          value: parseEmvValue(metrics.totalComments.emv?.current || '0'),
          count: metrics.totalComments.current,
          color: '#f59e0b'
        },
        {
          name: 'Shares',
          value: parseEmvValue(metrics.totalShares.emv?.current || '0'),
          count: metrics.totalShares.current,
          color: '#8b5cf6'
        }
      ];
    }

    // Fallback to local data (keep hardcoded multipliers for backward compatibility)
    const totalViews = filteredPosts.reduce((sum, p) => sum + p.views, 0);
    const totalLikes = filteredPosts.reduce((sum, p) => sum + p.likes, 0);
    const totalComments = filteredPosts.reduce((sum, p) => sum + p.comments, 0);
    const totalShares = filteredPosts.reduce((sum, p) => sum + p.shares, 0);

    return [
      { name: 'Views', value: totalViews * 0.01, count: totalViews, color: '#3b82f6' },
      { name: 'Likes', value: totalLikes * 0.10, count: totalLikes, color: '#10b981' },
      { name: 'Comments', value: totalComments * 0.50, count: totalComments, color: '#f59e0b' },
      { name: 'Shares', value: totalShares * 1.00, count: totalShares, color: '#8b5cf6' }
    ];
  }, [dashboardAnalytics, currentCustomerId, filteredPosts]);





  // Calculate growth rates
  const growthMetrics = useMemo(() => {
    if (emvTrendData.length < 2) return { emvGrowth: 0, postsGrowth: 0 };

    const recent = emvTrendData.slice(-7);
    const previous = emvTrendData.slice(-14, -7);

    const recentEMV = recent.reduce((sum: number, d: any) => sum + d.emv, 0);
    const previousEMV = previous.reduce((sum: number, d: any) => sum + d.emv, 0);
    const recentPosts = recent.reduce((sum: number, d: any) => sum + d.posts, 0);
    const previousPosts = previous.reduce((sum: number, d: any) => sum + d.posts, 0);

    return {
      emvGrowth: previousEMV > 0 ? ((recentEMV - previousEMV) / previousEMV) * 100 : 0,
      postsGrowth: previousPosts > 0 ? ((recentPosts - previousPosts) / previousPosts) * 100 : 0
    };
  }, [emvTrendData]);

  const formatTooltipValue = (value: number, name: string) => {
    if (name.toLowerCase().includes('emv')) {
      return formatEMV(value);
    }
    return value.toLocaleString();
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Calculate metrics for KPI cards - use API data when available
  const kpiMetrics = useMemo(() => {
    // Use dashboard analytics if available and customer is selected
    if (dashboardAnalytics && currentCustomerId) {
      const metrics = dashboardAnalytics.metrics;
      return {
        totalEmv: typeof metrics.totalEmv.current === 'string'
          ? parseFloat(metrics.totalEmv.current.replace(/[$,]/g, '')) || 0
          : parseFloat(metrics.totalEmv.current) || 0,
        totalPosts: metrics.totalPosts.current,
        averageEmv: typeof metrics.averageEmv.current === 'string'
          ? parseFloat(metrics.averageEmv.current.replace(/[$,]/g, '')) || 0
          : parseFloat(metrics.averageEmv.current) || 0,
        sponsoredPosts: 0, // API doesn't provide sponsored posts count separately
        sponsoredPercentage: 0
      };
    }
    
    // Fallback to local data
    const totalEmv = filteredPosts.reduce((sum, p) => sum + p.emv, 0);
    const sponsoredCount = filteredPosts.filter(p => p.sponsor).length;
    
    return {
      totalEmv,
      totalPosts: filteredPosts.length,
      averageEmv: filteredPosts.length > 0 ? totalEmv / filteredPosts.length : 0,
      sponsoredPosts: sponsoredCount,
      sponsoredPercentage: filteredPosts.length > 0 ? Math.round((sponsoredCount / filteredPosts.length) * 100) : 0
    };
  }, [dashboardAnalytics, currentCustomerId, filteredPosts]);


  // EMV Performance by Post Type data
  const postTypeComparisonData = useMemo(() => {
    if (!emvByPostTypeData) return [];

    const postTypes = emvByPostTypeData.byPostType;
    
    return Object.entries(postTypes).map(([postType, data]: [string, any]) => ({
      postType: postType,
      avgEmv: data.current.avgEmvValue,
      avgEmvFormatted: data.current.avgEmv,
      count: data.current.count,
      totalEmv: data.current.totalEmvValue
    }));
  }, [emvByPostTypeData]);

  // EMV by Author Type data processing for chart
  const authorTypeComparisonData = useMemo(() => {
    if (!emvByAuthorTypeData?.breakdown) return [];

    const { organic, collaborator } = emvByAuthorTypeData.breakdown;
    
    return [
      {
        origin: 'Organic',
        totalEmv: typeof organic.totalEmv === 'string'
          ? parseFloat(organic.totalEmv.replace(/[$,]/g, '')) || 0
          : parseFloat(organic.totalEmv) || 0,
        totalEmvFormatted: organic.totalEmv,
        postCount: organic.postCount,
        percentage: organic.percentageOfTotal,
        color: '#10b981' // green for organic
      },
      {
        origin: 'Collaborator',
        totalEmv: typeof collaborator.totalEmv === 'string'
          ? parseFloat(collaborator.totalEmv.replace(/[$,]/g, '')) || 0
          : parseFloat(collaborator.totalEmv) || 0,
        totalEmvFormatted: collaborator.totalEmv,
        postCount: collaborator.postCount,
        percentage: collaborator.percentageOfTotal,
        color: '#6366f1' // indigo for collaborator
      }
    ];
  }, [emvByAuthorTypeData]);

  // Period-over-Period Performance data
  const periodComparisonData = useMemo(() => {
    if (!emvByPostTypeData) return [];

    const postTypes = emvByPostTypeData.byPostType;
    
    return Object.entries(postTypes).map(([postType, data]: [string, any]) => ({
      postType: postType,
      current: data.current.totalEmv,
      previous: data.previous.totalEmv,
      change: data.changes.totalEmv,
      changeValue: parseFloat(data.changes.totalEmv.replace('%', ''))
    }));
  }, [emvByPostTypeData]);

  // Content Mix Optimization Insights
  const contentInsights = useMemo(() => {
    if (!emvByPostTypeData) return [];

    const insights = [];
    const topPerforming = emvByPostTypeData.insights.topPerformingPostType;
    const mostProductive = emvByPostTypeData.insights.mostProductivePostType;
    const lowestPerforming = emvByPostTypeData.insights.lowestPerformingPostType;

    // Top performing insight
    if (topPerforming) {
      const topData = emvByPostTypeData.byPostType[topPerforming];
      insights.push({
        title: `${topPerforming.charAt(0).toUpperCase() + topPerforming.slice(1)}s are your highest EMV generators`,
        description: `${topPerforming.charAt(0).toUpperCase() + topPerforming.slice(1)} content generates ${topData.current.avgEmv} average EMV per post.`,
        metric: `${topData.current.count} posts • ${topData.current.totalEmv} total EMV`,
        icon: <TrendingUp className="h-4 w-4 text-green-600" />,
        color: 'border-l-green-500',
        bgColor: 'bg-green-100'
      });
    }

    // Most productive insight
    if (mostProductive.type && mostProductive.type !== topPerforming) {
      const productiveData = emvByPostTypeData.byPostType[mostProductive.type];
      insights.push({
        title: `${mostProductive.type.charAt(0).toUpperCase() + mostProductive.type.slice(1)}s are your most frequent content`,
        description: `You've published ${mostProductive.count} ${mostProductive.type} posts, making up the majority of your content.`,
        metric: `${productiveData.current.avgEmv} average EMV`,
        icon: <BarChart3 className="h-4 w-4 text-blue-600" />,
        color: 'border-l-blue-500',
        bgColor: 'bg-blue-100'
      });
    }

    // Optimization opportunity
    if (lowestPerforming && lowestPerforming !== topPerforming) {
      const lowData = emvByPostTypeData.byPostType[lowestPerforming];
      if (lowData.current.count > 0) {
        insights.push({
          title: `Consider optimizing your ${lowestPerforming} content strategy`,
          description: `${lowestPerforming.charAt(0).toUpperCase() + lowestPerforming.slice(1)} posts have the lowest average EMV and may need improvement.`,
          metric: `${lowData.current.avgEmv} average EMV • ${lowData.current.count} posts`,
          icon: <AlertCircle className="h-4 w-4 text-orange-600" />,
          color: 'border-l-orange-500',
          bgColor: 'bg-orange-100'
        });
      }
    }

    // Period comparison insight
    const summary = emvByPostTypeData.summary;
    if (summary.changes.totalEmv) {
      const changeValue = parseFloat(summary.changes.totalEmv.replace('%', ''));
      insights.push({
        title: changeValue >= 0 ? 'EMV is trending upward' : 'EMV has declined this period',
        description: `Total EMV has ${changeValue >= 0 ? 'increased' : 'decreased'} by ${Math.abs(changeValue).toFixed(1)}% compared to the previous period.`,
        metric: `${summary.current.totalEmv} current vs ${summary.previous.totalEmv} previous`,
        icon: changeValue >= 0 ? 
          <ArrowUpRight className="h-4 w-4 text-green-600" /> : 
          <ArrowDownRight className="h-4 w-4 text-red-600" />,
        color: changeValue >= 0 ? 'border-l-green-500' : 'border-l-red-500',
        bgColor: changeValue >= 0 ? 'bg-green-100' : 'bg-red-100'
      });
    }

    return insights;
  }, [emvByPostTypeData]);

  // Post Type Efficiency Score calculation
  // Calculate optimal x-axis interval to show maximum 30 labels
  const calculateOptimalInterval = (dataLength: number, maxLabels: number = 30) => {
    if (dataLength <= maxLabels) return 0; // Show all labels if data is small enough
    return Math.ceil(dataLength / maxLabels) - 1; // Calculate interval to limit labels
  };

  const efficiencyScores = useMemo(() => {
    if (!emvByPostTypeData) return [];

    const postTypes = emvByPostTypeData.byPostType;
    const scores = Object.entries(postTypes).map(([postType, data]: [string, any]) => {
      // Safely parse EMV values - handle both string and number formats
      const avgEmvRaw = data.current.avgEmvValue || data.current.avgEmv || 0;
      const avgEmv = typeof avgEmvRaw === 'string' 
        ? parseFloat(avgEmvRaw.replace('$', '').replace(',', '')) 
        : parseFloat(avgEmvRaw) || 0;
      
      const count = data.current.count || 1;
      
      // Calculate total engagement from the API data if available
      const totalLikes = data.current.totalLikes || 0;
      const totalComments = data.current.totalComments || 0;
      const totalShares = data.current.totalShares || 0;
      const totalViews = data.current.totalViews || 0;
      
      const totalEngagement = totalLikes + totalComments + totalShares;
      const avgEngagement = totalEngagement / count;
      const avgViews = totalViews / count;
      
      // Calculate efficiency metrics
      const emvPerEngagement = avgEngagement > 0 ? avgEmv / avgEngagement : 0;
      const emvPerView = avgViews > 0 ? avgEmv / avgViews : 0;
      const engagementRate = avgViews > 0 ? (avgEngagement / avgViews) * 100 : 0;
      
      // Calculate overall efficiency score (weighted combination) and normalize to 1-20 scale
      const rawScore = (emvPerEngagement * 0.4) + (emvPerView * 1000 * 0.3) + (engagementRate * 0.3);
      
      // Normalize to 1-20 scale using logarithmic scaling to handle wide range of values
      const normalizedScore = Math.min(20, Math.max(1, 1 + (Math.log10(Math.max(0.1, rawScore)) + 1) * 9.5));
      const efficiencyScore = normalizedScore;
      
      // Safely parse total EMV
      const totalEmvRaw = data.current.totalEmvValue || data.current.totalEmv || 0;
      const totalEmv = typeof totalEmvRaw === 'string'
        ? parseFloat(totalEmvRaw.replace('$', '').replace(',', ''))
        : parseFloat(totalEmvRaw) || 0;
      
      return {
        postType,
        avgEmv,
        count,
        avgEngagement: Math.round(avgEngagement),
        avgViews: Math.round(avgViews),
        emvPerEngagement: emvPerEngagement.toFixed(3),
        emvPerView: emvPerView.toFixed(4),
        engagementRate: engagementRate.toFixed(2),
        efficiencyScore: efficiencyScore.toFixed(1),
        totalEmv
      };
    });

    // Sort by efficiency score
    return scores.sort((a, b) => parseFloat(b.efficiencyScore) - parseFloat(a.efficiencyScore));
  }, [emvByPostTypeData]);


  // const handleProfileToggle = (username: string) => {
  //   setSelectedProfiles(prev => 
  //     prev.includes(username) 
  //       ? prev.filter(p => p !== username)
  //       : [...prev, username]
  //   );
  // };

  // const selectAllProfiles = () => {
  //   setSelectedProfiles(profiles.map(p => p.username));
  // };

  // const clearAllProfiles = () => {
  //   setSelectedProfiles([]);
  // };

  return (
    !dashboardAnalytics ? (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-avi-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics charts...</p>
        </div>
      </div>
    ) : (
    <div className="space-y-6">

          {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="p-6 bg-gradient-to-br from-white to-green-50/30 border-green-100 hover:shadow-lg hover:border-green-200 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total EMV</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatEMV(kpiMetrics.totalEmv)}
              </p>
              <div className="flex items-center mt-2">
                {growthMetrics.emvGrowth >= 0 ? (
                  <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
                )}
                <span className={`text-sm font-medium ${
                  growthMetrics.emvGrowth >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {Math.abs(growthMetrics.emvGrowth).toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-white to-blue-50/30 border-blue-100 hover:shadow-lg hover:border-blue-200 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Posts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {kpiMetrics.totalPosts.toLocaleString()}
              </p>
              <div className="flex items-center mt-2">
                {growthMetrics.postsGrowth >= 0 ? (
                  <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                ) : (
                  <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
                )}
                <span className={`text-sm font-medium ${
                  growthMetrics.postsGrowth >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {Math.abs(growthMetrics.postsGrowth).toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <BarChart3 className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-white to-purple-50/30 border-purple-100 hover:shadow-lg hover:border-purple-200 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average EMV</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatEMV(kpiMetrics.averageEmv)}
              </p>
              <p className="text-sm text-gray-500 mt-2">per post</p>
            </div>
            <div className="p-3 rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-white to-indigo-50/30 border-indigo-100 hover:shadow-lg hover:border-indigo-200 transition-all duration-300 group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Sponsored Posts</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {kpiMetrics.sponsoredPosts}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {kpiMetrics.sponsoredPercentage}% of total
              </p>
            </div>
            <div className="p-3 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
              <PieChartIcon className="h-6 w-6 text-white" />
            </div>
          </div>
        </Card>
      </div>

      {/* EMV Trend Chart */}
      <Card className="p-6 bg-gradient-to-br from-white to-green-50/30 border-green-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">EMV Trend Over Time</h3>
            <p className="text-sm text-gray-600 mt-1">Track your earned media value performance</p>
          </div>
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-green-100 rounded-lg">
            <TrendingUp className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">Growth Trend</span>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={emvTrendData} margin={{ top: 10, right: 30, left: 80, bottom: 0 }}>
              <defs>
                <linearGradient id="emvGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
              <XAxis
                dataKey="date"
                fontSize={11}
                tick={{ fill: '#64748b' }}
                axisLine={{ stroke: '#cbd5e1' }}
                tickLine={{ stroke: '#cbd5e1' }}
                interval={calculateOptimalInterval(emvTrendData?.length || 0)}
                angle={-45}
                textAnchor="end"
                height={50}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  });
                }}
              />
              <YAxis
                tickFormatter={(value) => {
                  if (value >= 1000) {
                    return `$${(value / 1000).toFixed(0)}k`;
                  }
                  return `$${value.toFixed(2)}`;
                }}
                fontSize={12}
                width={70}
                tick={{ fill: '#64748b' }}
                axisLine={{ stroke: '#cbd5e1' }}
                tickLine={{ stroke: '#cbd5e1' }}
              />
              <Tooltip
                formatter={(value, name) => [formatTooltipValue(value as number, name as string), name]}
                labelStyle={{ color: '#111827', fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '13px',
                  padding: '12px 16px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                }}
                cursor={{ stroke: '#10b981', strokeWidth: 1, strokeDasharray: '5 5' }}
              />
              <Area
                type="monotone"
                dataKey="emv"
                stroke="#10b981"
                strokeWidth={3}
                fill="url(#emvGradient)"
                name="EMV"
                animationDuration={1500}
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4, stroke: '#fff' }}
                activeDot={{ r: 6, fill: '#10b981', stroke: '#fff', strokeWidth: 3, filter: 'url(#glow)' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Content Type Efficiency Scores */}
      <Card className="p-6 bg-gradient-to-br from-white to-blue-50/30 border-blue-100 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Content Type Efficiency Scores</h3>
          <div className="group relative">
            <HelpCircle className="h-5 w-5 text-blue-500 hover:text-blue-600 cursor-help transition-colors" />
            <div className="invisible group-hover:visible absolute left-0 top-7 z-50 w-96 p-4 bg-white border border-blue-200 rounded-lg shadow-xl">
              <h4 className="font-semibold text-gray-900 mb-2 text-sm flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-blue-500" />
                How Efficiency Score is Calculated
              </h4>
              <p className="text-xs text-gray-600 mb-3">
                The efficiency score combines multiple factors to rank content types by their overall performance:
              </p>
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>EMV per Engagement (40%)</strong> - Dollar value per like/comment/share</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-1 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>EMV per View (30%)</strong> - Dollar value per view</span>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full mt-1 flex-shrink-0"></div>
                  <span className="text-gray-700"><strong>Engagement Rate (30%)</strong> - Percentage of views that engage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-900">Content Type</th>
                <th className="text-center py-2 px-3 text-sm font-semibold text-gray-900">Efficiency Score</th>
                <th className="text-center py-2 px-3 text-sm font-semibold text-gray-900">EMV/Engagement</th>
                <th className="text-center py-2 px-3 text-sm font-semibold text-gray-900">EMV/View</th>
                <th className="text-center py-2 px-3 text-sm font-semibold text-gray-900">Engagement Rate</th>
                <th className="text-center py-2 px-3 text-sm font-semibold text-gray-900">Avg EMV</th>
                <th className="text-center py-2 px-3 text-sm font-semibold text-gray-900">Posts</th>
              </tr>
            </thead>
            <tbody>
              {efficiencyScores.map((score) => {
                const efficiencyRank = parseFloat(score.efficiencyScore);
                
                // Determine row color based on actual efficiency score ranges
                const getRowColorClass = (scoreValue: number) => {
                  if (scoreValue >= 13) {
                    return 'bg-green-50 hover:bg-green-100'; // Excellent (13-20)
                  } else if (scoreValue >= 6) {
                    return 'bg-yellow-50 hover:bg-yellow-100'; // Good (6-12)
                  } else {
                    return 'bg-red-50 hover:bg-red-100'; // Poor (1-5)
                  }
                };
                
                return (
                  <tr key={score.postType} className={`border-b border-gray-100 ${getRowColorClass(efficiencyRank)}`}>
                    <td className="py-3 px-3">
                      <p className="text-sm font-medium text-gray-900 capitalize">{score.postType}</p>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <div className="flex items-center justify-center">
                        <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          efficiencyRank >= 13 
                            ? 'bg-green-100 text-green-800' 
                            : efficiencyRank >= 6 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {parseFloat(score.efficiencyScore).toFixed(1)}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="text-xs font-medium text-gray-900">${score.emvPerEngagement}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="text-xs font-medium text-gray-900">${score.emvPerView}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="text-xs font-medium text-gray-900">{score.engagementRate}%</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="text-xs font-medium text-gray-900">{formatEMV(score.avgEmv)}</span>
                    </td>
                    <td className="py-3 px-3 text-center">
                      <span className="text-xs text-gray-600">{score.count}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Performance Scale Key */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-3">Performance Scale (1-20)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="flex items-center space-x-3">
              <div className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                1-5
              </div>
              <span className="text-sm font-medium text-gray-700">Poor Performance</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
                6-12
              </div>
              <span className="text-sm font-medium text-gray-700">Good Performance</span>
            </div>
            <div className="flex items-center space-x-3">
              <div className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                13-20
              </div>
              <span className="text-sm font-medium text-gray-700">Excellent Performance</span>
            </div>
          </div>
        </div>

      </Card>

      {/* EMV by Post Type Analysis */}
      <div className="space-y-6">
        {/* EMV Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-3">EMV Performance by Post Type</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={postTypeComparisonData} margin={{ top: 10, right: 20, left: 60, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="postType" 
                    fontSize={11}
                    tick={{ fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                  />
                  <YAxis 
                    tickFormatter={(value) => {
                      if (value >= 1000) {
                        return `$${(value / 1000).toFixed(0)}k`;
                      }
                      return `$${value}`;
                    }}
                    fontSize={11}
                    tick={{ fill: '#64748b' }}
                    axisLine={{ stroke: '#e2e8f0' }}
                    width={55}
                  />
                  <Tooltip 
                    formatter={(value, name, props) => {
                      if (name === 'Avg EMV') {
                        // Use the pre-formatted avgEmvFormatted from the data payload
                        return [props.payload?.avgEmvFormatted || value, 'Avg EMV'];
                      }
                      return [formatNumber(value as number), name === 'Post Count' ? 'Posts' : name];
                    }}
                    labelStyle={{ color: '#374151', fontSize: '12px', fontWeight: '500' }}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '6px', 
                      fontSize: '12px',
                      padding: '8px 12px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Bar dataKey="avgEmv" fill="#3b82f6" name="Avg EMV" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="count" fill="#10b981" name="Post Count" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex justify-center gap-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-xs text-gray-600">Average EMV</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-xs text-gray-600">Post Count</span>
              </div>
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-3">EMV Breakdown by Source</h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={emvBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={85}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {emvBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatEMV(value as number)}
                    labelStyle={{ color: '#374151', fontSize: '12px', fontWeight: '500' }}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: '6px', 
                      fontSize: '12px',
                      padding: '8px 12px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {emvBreakdownData.map((item) => (
                <div key={item.name} className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: item.color }}
                  />
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">{item.name}</p>
                    <p className="text-xs text-gray-500">{formatNumber(item.count)} • {formatEMV(item.value)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-3">EMV by Post Origin</h3>
            {authorTypeComparisonData.length > 0 ? (
              <>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={authorTypeComparisonData} margin={{ top: 10, right: 20, left: 60, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis 
                        dataKey="origin" 
                        tick={{ fontSize: 12, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={{ stroke: '#d1d5db' }}
                        axisLine={{ stroke: '#d1d5db' }}
                        tickFormatter={(value) => formatEMV(value)}
                      />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === 'Total EMV') return [formatEMV(value as number), name];
                          return [value, name];
                        }}
                        labelStyle={{ color: '#374151', fontSize: '12px', fontWeight: '500' }}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '6px', 
                          fontSize: '12px',
                          padding: '8px 12px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Bar 
                        dataKey="totalEmv" 
                        name="Total EMV" 
                        radius={[4, 4, 0, 0]}
                      >
                        {authorTypeComparisonData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    {authorTypeComparisonData.map((item) => (
                      <div key={item.origin} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center justify-center space-x-2 mb-1">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-sm font-medium text-gray-900">{item.origin}</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">{item.totalEmvFormatted}</p>
                        <p className="text-xs text-gray-500">{item.postCount} posts • {item.percentage}%</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No data available</p>
                  <p className="text-xs mt-1">Data will appear when posts are analyzed</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Period-over-Period Performance */}
        <Card className="p-6 bg-gradient-to-br from-white to-orange-50/30 border-orange-100 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">Period-over-Period Performance</h3>
            <div className="text-xs text-gray-500">Compare current vs previous period</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {periodComparisonData.map((item) => (
              <div key={item.postType} className="bg-white border border-gray-200 p-4 rounded-lg hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 capitalize">{item.postType}</h4>
                  <div className={`px-2 py-1 rounded-full text-xs font-bold ${
                    item.changeValue >= 0
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {item.change}
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Current</span>
                    <span className="text-sm font-semibold text-gray-900">{item.current}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Previous</span>
                    <span className="text-sm text-gray-600">{item.previous}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">Growth</span>
                      {item.changeValue >= 0 ? (
                        <div className="flex items-center text-green-600">
                          <ArrowUpRight className="h-3 w-3 mr-1" />
                          <span className="text-xs font-semibold">Trending Up</span>
                        </div>
                      ) : (
                        <div className="flex items-center text-red-600">
                          <ArrowDownRight className="h-3 w-3 mr-1" />
                          <span className="text-xs font-semibold">Trending Down</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Content Mix Optimization Insights */}
        <Card className="p-6 bg-gradient-to-br from-white to-teal-50/30 border-teal-100 hover:shadow-lg transition-all duration-300">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Content Mix Optimization Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {contentInsights.map((insight, index) => (
              <div key={index} className={`p-4 rounded-lg border-l-4 ${insight.color}`}>
                <div className="flex items-start space-x-3">
                  <div className={`p-1 rounded-full ${insight.bgColor}`}>
                    {insight.icon}
                  </div>
                  <div className="flex-1">
                    <h5 className="font-semibold text-gray-900 mb-1">{insight.title}</h5>
                    <p className="text-sm text-gray-600">{insight.description}</p>
                    {insight.metric && (
                      <p className="text-sm font-semibold text-gray-800 mt-1">{insight.metric}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
        </div>
    </div>
    )
  );
};

export default EMVCharts;
